// imports
import { Dispatch } from 'react';
import { UseFormSetError } from 'react-hook-form';
import { ForwardedRef, SetStateAction } from 'react';
import { EditorState } from 'draft-js';

// graphql interfaces
import { SelectType, StaffInputProps, StripeFormType, Nullable } from 'interfaces';
import {
  FormulaIngredientUnit,
  IngredientType,
  LoadAddUnit,
  MacroNutrientsWeights,
  ServingUnitType,
  StripePaymentMethod,
  StripePrice,
} from 'generated/graphql';

export type OrganizationFormType = {
  organizationName: string;
  maxUser: string;
};

export type StaffRoleFormType = {
  role: string;
};

export type UserFormType = {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
};

export type UpdatePasswordFormType = {
  password: string;
};

export type AddressFormType = {
  address1: string;
  address2: string;
  city: string;
  state: SelectType;
  zip: string;
  country: SelectType;
};

export type AddNewUserFormType = UserFormType &
  UpdatePasswordFormType &
  OrganizationFormType &
  AddressFormType;
export type EditUserFormType = OrganizationFormType & UserFormType;

export type EditUserAddressFormType = AddressFormType;

export type AddNewStaffFormType = StaffRoleFormType & UserFormType & UpdatePasswordFormType;
export type EditStaffFormType = StaffRoleFormType & UserFormType;
export type AddOrgStaffFormType = AddNewStaffFormType & {
  priceId: string;
  trialEnd: string;
};

export type IngredientBasicInfoCardType = {
  category: SelectType;
  subCategory: SelectType;
  name: string;
  scientificName: string;
  latinName: EditorState;
  tradeMarkName: string;
  productTypes: string[];
  type: IngredientType[];
  description: string;
  isOrganic: boolean;
};
export type SyrupBaseIngredientBasicInfoCardType = {
  name: string;
  type: IngredientType[];
  productFormulationSyrupType: SelectType;
};

export type IngredientCostInfoCardType = {
  amount: string;
  potency: string;
  cost: string;
  suggestedOverage: string;
  gummyOverage: string;
  shelfLife: string;
  nutrients: boolean;
  nutrientName: boolean;
  showB3: boolean;
  proBiotic: string;
};
export type SyrupBaseIngredientCostInfoCardType = {
  amount: string;
  gummyOverage: string;
  nutrients: boolean;
  nutrientName: boolean;
  showB3: boolean;
};

export type IngredientSupplierCardType = {
  primarySupplier: SelectType;
  secondarySupplier: SelectType;
  tertiarySupplier: SelectType;
};

export type IngredientCountriesCardType = {
  manufacturingCountry: SelectType;
  originCountry: SelectType;
};

export type IngredientDensityCardType = {
  averageParticleSize: string;
  lossOnDrying: string;
  density: string;
};

export type CheckoutFormType = {
  priceId: string;
  amount: StripePrice['amount'];
  interval: StripePrice['interval'];
  mode?: ModeType;
  staff?: StaffInputProps;
  isTrialEnded: StripePrice['isTrialEnded'];
  onComplete?: () => void;
  billAbleAmount: StripePrice['billAbleAmount'];
};

export type AllergensCardType = {
  allergens: string[];
};

export type CarrierSelectType = {
  excipient: SelectType;
  percentage: string;
};

export type CarriersFormType = {
  carriers: CarrierSelectType[];
};
export type SubComponentSelectType = {
  name: string;
  percentage: string;
};

export type SubComponentsFormType = {
  subComponents: SubComponentSelectType[];
};

export type NutrientsTypeObject = {
  organic: string;
  moisture: string;
  calories: string;
  protein: string;
  carbohydrates: string;
  dietaryFiber: string;
  totalSugars: string;
  addedSugar: string;
  totalFat: string;
  saturatedFat: string;
  monounsaturatedFat: string;
  polyunsaturatedFat: string;
  transFat: string;
  vitaminA: string;
  biotin: string;
  thiamin: string;
  riboflavin: string;
  niacin: string;
  vitaminB6: string;
  vitaminB12: string;
  vitaminC: string;
  vitaminD: string;
  vitaminE: string;
  magnesium: string;
  zinc: string;
  vitaminK2: string;
  choline: string;
  iodine: string;
  folate: string;
  cholesterol: string;
  calcium: string;
  iron: string;
  potassium: string;
  boron: string;
  sodium: string;
  chloride: string;
  chromium: string;
  copper: string;
  manganese: string;
  molybdenum: string;
  phosphorus: string;
  pantothenicAcid: string;
  selenium: string;
};

export type HeavyMetalsFormType = {
  arsenic: string;
  cadmium: string;
  lead: string;
  mercury: string;
};

export type NutrientsCardType = {
  ingredientInfo: NutrientsTypeObject;
};

export type AddNewIngredientFormType = IngredientBasicInfoCardType &
  IngredientCostInfoCardType &
  IngredientSupplierCardType &
  IngredientCountriesCardType &
  IngredientDensityCardType &
  HeavyMetalsFormType &
  AllergensCardType &
  NutrientsCardType &
  CarriersFormType &
  SubComponentsFormType;

export type AddNewSyrupBaseIngredientFormType = IngredientBasicInfoCardType &
  HeavyMetalsFormType &
  NutrientsCardType & {
    productFormulationSyrupType?: SelectType;
  };

export type ProductTypeSelect = SelectType & { type?: string };

export type IngredientItemFormType = Omit<
  IngredientItemType,
  'ingredientId' | 'type' | 'unit' | 'cost' | 'showCfu'
>;

export type IngredientItemType = {
  ingredientId: string;
  value: string;
  type: string;
  unit: FormulaIngredientUnit;
  name: string;
  potency: number;
  overage?: number;
  gummyOverage?: number;
  ingredientPotency: number;
  isActive?: boolean;
  showCfu: boolean;
  macroNutrientsWeight?: MacroNutrientsWeights;
  category?: { name: string };
  subCategory?: { name: string };
};

export type BlendItemType = {
  name: string;
  ingredients: IngredientItemType[];
};

export type DosageFormulationFormType = {
  // common
  bottleCap: string;
  bottleType: string;
  coMfgCostType: string;
  coMfgCostPercentage: string;
  coMfgCost: string;
  servingUnit: ServingUnitType;
  productType: string;
  productSubType: string;
  desiredServingSize: string;
  servingPerContainer: string;
  productFormulationType: ProductTypeSelect;

  ageGroups: FormulaAgeGroupSelectType[];

  beadlets: string;
  tabletType: string;

  capsuleType: string;
  innerCapsuleType: string;
  outerCapsuleType: string;

  colorsHex: string[];
  excipientLabel: string;
  excipientType: SelectType;

  miniTabFormulation: string;
  miscellaneousCost: string;
  description: string;

  // layer

  tabletCoating: string;
  coatingColor: string;

  // layer 2

  coatingColor2: string;

  // layer 3

  coatingColor3: string;

  flavorType: string;
  flavorSystem: SelectType;

  sweetenerType: string;
  sweetenerSystem: SelectType;

  veganType: string;
  factBoxType: string;
  proteinBase: string;

  // gummies

  productSize: SelectType;
  sandCoating: string;
};

export type AddNewFormulaFormType = {
  name: string;
  formulaBlend: BlendItemType[];
  ingredients: IngredientItemFormType[];
  formulaIngredients: IngredientItemType[];
  capOverride: boolean;
};

export type UpdateUserProfileType = {
  email: string;
  phone: string;
  lastName: string;
  firstName: string;
  orgName: string;
};

export type FactBoxFormulaFormType = Pick<DosageFormulationFormType, 'factBoxType'>;
export type FormulationFormType = Omit<
  DosageFormulationFormType,
  'productType' | 'productFormulationType' | 'productSubType'
>;

export type FactBoxIngredientFormType = Omit<AddNewFormulaFormType, 'name' | 'formulaBlend'>;

export type FactBoxFormulationFormType = FactBoxFormulaFormType & FactBoxIngredientFormType;
export type FormulaIngredientFormType = FactBoxIngredientFormType & {
  primary: SelectType;
  secondary: SelectType;
};

export type FormulaInfoCardFormType = Pick<AddNewFormulaFormType, 'name'>;

export type FormulaFormDataForStep1InLocalStorage = Pick<
  AddNewFormulaFormType,
  'name' | 'formulaIngredients'
>;

export type FormulaFormDataInLocalStorageType = Omit<
  DosageFormulationFormType,
  'productSubType' | 'productType'
> &
  FormulaFormDataForStep1InLocalStorage & {
    productSubType: ProductTypeSelect;
    productType: SelectType;
    formulaBlends: BlendItemType[];
  };

export type SaveFormulaAsDraftFormType = {
  name: string;
};

export type AdditionalContactSelectType = {
  name: string;
  email: string;
};

export type ContactFormType = {
  id?: string;
  name: string;
  city: string;
  email: string;
  phoneNo: string;
  zipCode: string;
  address2: string;
  address1: string;
  state: SelectType;
  country: SelectType;
  designation: string;
};

export type SupplierFormType = {
  companyName: string;
  description: string;
};

export type AdditionalContactFormType = {
  additionalContacts: AdditionalContactSelectType[];
};

export type FormulaAgeGroupSelectType = {
  ageGroupType: string;
  dvType: string;
  servingSize: string;
  servingUnit: string;
  servingContainer: string;
};

export type AddSupplierFormType = SupplierFormType & ContactFormType & AdditionalContactFormType;

export type AddProductTypeFormType = {
  name: string;
  // Super-admin only: opt-in to copy this type to organizations that lack it.
  propagateToOrganizations?: boolean;
};

export type AddSubProductTypeFormType = {
  name: string;
  productType: SelectType;
};

export type AddProductSizeFormType = {
  name: string;
  productType: SelectType;
  subProductType: SelectType;
  emptyWtMg: string;
  min: string;
  max: string;
  gummyWeight: string;
};

export type AddProductFormulationTypeForm = {
  name: string;
  isActive: boolean;
  lohInside: string;
  conversionCost: string;
  isInnerCapTab: boolean;
  innerComponentCost: string;
  productType: SelectType;
  productSubType: SelectType;
  // Super-admin only: opt-in to copy this formulation to organizations that lack it.
  propagateToOrganizations?: boolean;
};

export type AddExcipientFormType = {
  name: string;
};

export type AddExcipientLabelFormType = {
  name: string;
};

export type AddExcipientTypeFormType = {
  name: string;
  loadAdd: string;
  loadAddUnit: LoadAddUnit;
  perKgCost: string;
  excipientLabel: string;
  productType: SelectType;
};

export type ProductExcipientFormType = {
  perKgCost: string;
  percentage: string;
  excipient: SelectType;
  suppliers: SelectType[];
};

export type CoatingFormulationComponentFormType = {
  weight: string;
  sandComponent: SelectType;
  sandCoatingFormulation: SelectType;
};

export type AddBottleSizeFormType = {
  name: string;
  lidSize: string;
  cc: string;
  oz: string;
  productTypes: SelectType[];
};

export type AddServingSizeFormType = {
  size: string;
};

export type AddBottlePriceFormType = {
  isCr: boolean;
  price: string;
  type: string;
  bottleSize: SelectType;
  suppliers: SelectType[];
};

export type AddBottleCapacityFormType = {
  lohCost: string;
  fillingPercentage: string;
  bottleSize: SelectType;
  productType: SelectType;
  productSize: SelectType;
  servingSize: SelectType;
};

export type AddSweetenerSystemFormType = {
  name: string;
  nature: string;
  size: string;
  mgPerServing: string;
  costPerServing: string;
  type: string;
  productType: SelectType;
  productSize: SelectType;
  productFormulationType: SelectType;
};

export type AddSweetenerComponentFormType = {
  name: string;
  costPerKg: string;
};

export type ProductIngredientFormType = {
  name: string;
};

export type SandCoatingFormType = {
  name: string;
};

export type SandCoatingFormulationFormType = {
  sandCoating: SelectType;
  productSize: SelectType;
  productType: SelectType;
  subProductType: SelectType;
  weight: string;
  cost: string;
};

export type SandComponentFormType = {
  name: string;
};

export type AddSweetenerElementFormType = {
  qtPerMg: string;
  sweetenerComponent: SelectType;
  sweetenerSystem: SelectType;
};

export type FormulationSyrupTypeFormType = {
  name: string;
  cost: string;
  weight: string;
  productType: SelectType;
  productSize: SelectType;
  subProductType: SelectType;
  productFormulationType: SelectType;
};

export type IngredientSyrupTypeFormType = {
  percentage: string;
  productIngredient: SelectType;
};

export type CapsuleTypeFormType = {
  name: string;
  description: string;
};

export type CapsulePriceFormType = {
  price: string;
  capacity: string;

  density: SelectType;
  supplier: SelectType;
  capsuleType: SelectType;
  productSize: SelectType;
};

export type BaseCoatingFormType = {
  name: string;
  loadAdd: string;
  costPerKg: string;
  productType: SelectType;
};

export type AddCardFormType = {
  formLoading: boolean;
  setFormLoading: (formLoading: boolean) => void;
  onComplete?: () => void;
  onCancel?: () => void;
};

export enum ModeType {
  UPGRADE = 'upgrade',
  ACTIVATE_STAFF = 'activate-staff',
  CREATE_ADMIN = 'create-admin',
  CREATE_STAFF = 'create-staff',
}

export type ExistingCardRef = ForwardedRef<{
  submit: () => void;
}>;

export type ExistingCardFormType = {
  setSelectedCard: (selectedCard: Nullable<StripePaymentMethod>) => void;
  selectedCard: Nullable<StripePaymentMethod>;
  setFormLoading: (formLoading: boolean) => void;
  onComplete?: () => void;
  openAddCardForm: () => void;
  priceId: string;
  mode?: ModeType;
  staff?: StaffInputProps;
  ref?: ExistingCardRef;
  isTrialEnded?: Nullable<boolean>;
};

export type CardCompletionStatus = {
  card: boolean;
  expiry: boolean;
  cvc: boolean;
};

export type StripeCardChangeProps = {
  setIsCardCompleted?: Dispatch<SetStateAction<CardCompletionStatus>>;
  setError: UseFormSetError<StripeFormType>;
};

export type StripeCheckoutFormType = {
  setIsCardCompleted?: Dispatch<SetStateAction<CardCompletionStatus>>;
};

export type DvConstraintsFormType = {
  name: string;
  foodComponent: string;
  infants: string;
  adultsAndChildren: string;
  children1To3Years: string;
  pregnantAndLactatingWomen: string;
  unit: string;
};
